<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useResponsive } from "@core/composables"
import type {
    FormPaymentUserForm,
    FormPaymentCompanyForm,
} from "@modules/abonnement/types/subscription"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            collect_data:
                "Collecte et analyse des données énergétiques en temps réel",
            our_customer: "Nos clients nous font confiance",
            bullet_consent: "Dématérialisation des mandats de consentement",
            bullet_data:
                "Récupération jusqu'à 36 mois des données de consommation",
            bullet_export: "Export des données au format Excel",
            bullet_users:
                "Utilisateurs illimités - Onboarding et support inclus",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (
        e: "submit",
        payload: {
            alreadyExist: false
            user: FormPaymentUserForm & { phone: string }
            company: FormPaymentCompanyForm
        }
    ): void
    (
        e: "update:pipedrive",
        payload: {
            contactId?: string
            organisationId?: string
            dealId?: string
        }
    ): void
}>()

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()

// ================================
// STATE
// ================================
/** Logos clients affichés (marques partenaires). */
const customerLogos = ref<{ name: string; logo: string }[]>([
    {
        name: "Eurométropole Strasbourg",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_Eurome%CC%81tropole-Strasbourg.png",
    },
    {
        name: "Bureau Veritas",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_Bureau-Veritas.png",
    },
    {
        name: "Big Mamma",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_big-mamma.png",
    },
    {
        name: "Paris Habitat",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_paris-habitat.png",
    },
    {
        name: "vacanceole",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_vacanceole.png",
    },
    {
        name: "idex",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_idex.png",
    },
    {
        name: "bouygues",
        logo: "https://homeys-web-assets.s3.amazonaws.com/logo_bouygues.svg.png",
    },
])

// ================================
// COMPUTED
// ================================
/** Points d'accroche affichés (avantages de l'offre). */
const getBulletPoints = computed(() => [
    { point: t("bullet_consent") },
    { point: t("bullet_data") },
    { point: t("bullet_export") },
    { point: t("bullet_users") },
])

// ================================
// INTERACTIONS
// ================================
/** Soumission du formulaire utilisateur / entreprise. */
function onUserSubmit(payload: {
    alreadyExist: false
    user: FormPaymentUserForm & { phone: string }
    company: FormPaymentCompanyForm
}): void {
    emit("submit", payload)
}

/** Met à jour le contexte Pipedrive (contact, organisation, deal). */
function onUpdatePipedrive(payload: {
    contactId?: string
    organisationId?: string
    dealId?: string
}): void {
    emit("update:pipedrive", payload)
}
</script>

<template>
    <div
        class="subscription-form-intro flex flex-col justify-center items-center gap-12 w-full"
    >
        <!-- Content : gauche (desktop) + droite (formulaire + clients) -->
        <div
            class="flex flex-col md:flex-row justify-center items-start gap-4 self-stretch"
        >
            <!-- Right: formulaire + logos clients -->
            <SubscriptionFormUser
                class="w-full"
                @submit="onUserSubmit"
                @update:pipedrive="onUpdatePipedrive"
            />
            <!-- Left: title, mockup, bullet points (hidden on mobile) -->
            <div
                v-if="!isMobile"
                class="flex flex-col justify-center items-center gap-4 flex-1"
            >
                <h1
                    class="text-center py-4 text-xl font-semibold text-primary-700"
                >
                    {{ t("collect_data") }}
                </h1>
                <div
                    class="w-[584px] h-[371px] relative flex items-center justify-center overflow-hidden"
                >
                    <img
                        src="https://homeys-web-assets.s3.amazonaws.com/mockup_parcours_inscription.png"
                        alt=""
                        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[880px] max-w-none h-[587px] object-contain"
                    />
                </div>
                <ul
                    class="flex flex-col justify-center items-stretch self-stretch list-none gap-2 mx-auto"
                >
                    <li
                        v-for="(item, index) in getBulletPoints"
                        :key="index"
                        class="flex items-center gap-1 self-stretch"
                    >
                        <BaseIcon
                            icon="pi pi-check"
                            class="shrink-0 w-4 h-4 text-primary-600"
                        />
                        <span class="text-primary-900">{{ item.point }}</span>
                    </li>
                </ul>
            </div>
        </div>
        <div
            class="flex flex-col justify-center items-center gap-6 self-stretch"
        >
            <p class="text-base font-medium text-primary-700">
                {{ t("our_customer") }}
            </p>
            <div
                class="flex flex-row flex-wrap justify-center items-center gap-8"
            >
                <img
                    v-for="(logo, index) in customerLogos"
                    :key="index"
                    :src="logo.logo"
                    :alt="logo.name"
                    class="max-h-[50px] max-w-[75px] object-contain"
                />
            </div>
        </div>
    </div>
</template>
